import { Icon, spacing } from '@scality/core-ui';
import { Button } from '@scality/core-ui/dist/next';
import type React from 'react';
import { type ReactNode, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { useChat } from '../../hooks/useChat';
import { useIsDashboardMode } from '../../hooks/useIsDashboardMode';
import { useMcpBridge } from '../../mcp/McpBridgeProvider';
import { CONVERSATION_PATH } from '../../routes/paths';
import { GuardianContext } from '../GuardianProvider';
import { SidebarModeTabs } from './SidebarModeTabs';
import { SidebarShell } from './SidebarShell';

/* Left-aligned in both states: its centre used to travel the width of the rail between them. */
const ToggleRow = styled.div<{ $expanded: boolean }>`
  display: flex;
  justify-content: ${({ $expanded }) => ($expanded ? 'flex-start' : 'center')};
  padding: ${spacing.r8} ${spacing.r4};
`;

/** New-conversation action on the closed rail (the expanded body has its own full-width one). */
const CollapsedActionRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 0 ${spacing.r4};
`;

const NewConversationBox = styled.div`
  padding: 0 ${spacing.r8} ${spacing.r8};

  /* Full-width primary action under the toggle. */
  > * {
    width: 100%;
  }
`;

const FullWidthButton = styled(Button)`
  width: 100%;
`;

type AppSidebarProps = {
  /** Mode-specific body (conversation history, dashboard list, …), shown when expanded. */
  body: ReactNode;
};

/**
 * The app sidebar shared by every mode: the collapse toggle, the mode switch, and the
 * "new conversation" action. The body below the switch is mode-specific and supplied by the
 * route's layout — it is the only part that waits for the sidebar to be expanded.
 */
export const AppSidebar: React.FC<AppSidebarProps> = ({ body }) => {
  const { isInIframe } = useMcpBridge();
  const { setConversationId } = useContext(GuardianContext);
  const navigate = useNavigate();

  const handleNewConversation = useCallback(() => {
    setConversationId(uuidv4());
    navigate(CONVERSATION_PATH);
  }, [setConversationId, navigate]);

  const isDashboard = useIsDashboardMode();
  // Nothing to start when the current conversation is already empty.
  const { conversation } = useChat();
  const isUserOnNewChat = conversation.isNew;

  return (
    <SidebarShell>
      {({ isExpanded, setIsExpanded }) => {
        // Embedded (MCP host): the drawer is driven by the navbar, so skip the toggle and tabs.
        if (isInIframe) {
          return body;
        }
        return (
          <>
            <ToggleRow $expanded={isExpanded}>
              <Button
                icon={<Icon name="Sidebar" />}
                onClick={() => setIsExpanded((prev) => !prev)}
                tooltip={{ overlay: isExpanded ? 'Collapse sidebar' : 'Expand sidebar' }}
              />
            </ToggleRow>
            <SidebarModeTabs isExpanded={isExpanded} />
            {!isExpanded && !isDashboard && (
              <CollapsedActionRow>
                <Button
                  icon={<Icon name="Create-add" />}
                  onClick={handleNewConversation}
                  disabled={isUserOnNewChat}
                  tooltip={{ overlay: 'Start new conversation' }}
                />
              </CollapsedActionRow>
            )}
            {isExpanded && (
              <>
                {!isDashboard && (
                  <NewConversationBox>
                    <FullWidthButton
                      label="Start new conversation"
                      icon={<Icon name="Create-add" />}
                      variant="outline"
                      onClick={handleNewConversation}
                      disabled={isUserOnNewChat}
                    />
                  </NewConversationBox>
                )}
                {body}
              </>
            )}
          </>
        );
      }}
    </SidebarShell>
  );
};
